import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMe } from "@/context/MeContext";
import { useCart } from "@/context/CartContext";
import { errMsg, eur, publicGet, useApi } from "@/lib/api";
import { PointsSelector, discountFor, maxPointsFor } from "@/components/club/PointsSelector";
import { PaymentMethods } from "@/components/club/PaymentMethods";
import { AddressFields, emptyAddress } from "@/components/search/AddressFields";
import { SummaryRow } from "@/components/search/FateloVoiDialog";

export default function Checkout() {
  const { state } = useLocation();
  const { me, refresh } = useMe();
  const { clear } = useCart();
  const api = useApi();
  const navigate = useNavigate();
  const items = useMemo(() => state?.items || [], [state]);
  const [partners, setPartners] = useState({});
  const [delivery, setDelivery] = useState({});
  const [address, setAddress] = useState({ ...emptyAddress, full_name: me?.full_name || "" });
  const [phone, setPhone] = useState(me?.phone || "");
  const [enabled, setEnabled] = useState(false);
  const [points, setPoints] = useState(0);
  const [busy, setBusy] = useState(false);
  const status = me?.status || "bronze";

  useEffect(() => {
    publicGet("/marketplace/partners").then((ps) => setPartners(Object.fromEntries(ps.map((p) => [p.id, p])))).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const g = {};
    items.forEach((i) => (g[i.offer.partner_id] = g[i.offer.partner_id] || []).push(i));
    return g;
  }, [items]);

  const subtotal = items.reduce((s, i) => s + i.offer[`price_${status}`] * i.qty, 0);
  const groupSub = (pid) => groups[pid].reduce((s, i) => s + i.offer[`price_${status}`] * i.qty, 0);
  const shipFor = (pid) => {
    const p = partners[pid];
    if ((delivery[pid] || "shipping") !== "shipping" || !p) return 0;
    return p.free_shipping_over && groupSub(pid) >= p.free_shipping_over ? 0 : Number(p.shipping_cost || 0);
  };
  const shipping = Object.keys(groups).reduce((s, pid) => s + shipFor(pid), 0);
  const total = Math.round((subtotal + shipping) * 100) / 100;
  const discount = enabled && me ? discountFor(points, me.redeem_rate, total) : 0;
  const needsAddress = Object.keys(groups).some((pid) => (delivery[pid] || "shipping") === "shipping");

  useEffect(() => {
    if (state?.usePoints && me && Object.keys(partners).length) {
      const max = maxPointsFor(me.balance, me.redeem_rate, total);
      if (max > 0) { setEnabled(true); setPoints(max); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, partners]);

  if (!items.length) return <Navigate to="/carrello" replace />;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api("post", "/orders/marketplace", {
        items: items.map((i) => ({ offer_id: i.offer.id, qty: i.qty })),
        delivery: Object.fromEntries(Object.keys(groups).map((pid) => [pid, delivery[pid] || "shipping"])),
        address: needsAddress ? address : null, phone, points_to_use: enabled ? points : 0,
      });
      if (!state?.direct) clear();
      await refresh();
      toast.success("Ordine creato! SMS di conferma inviato a te e all'Admin.");
      navigate("/ordine-confermato", { state: { orders: res.orders } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[1.3fr_1fr]" data-testid="checkout-page">
      <div className="space-y-6">
        <h1 className="font-display text-4xl font-extrabold text-white">Completa l'ordine</h1>
        {Object.entries(groups).map(([pid, its]) => (
          <div key={pid} className="rounded-2xl border border-white/10 bg-[#12141C] p-5" data-testid={`checkout-group-${pid}`}>
            <p className="font-semibold text-white">{partners[pid]?.company_name || its[0].offer.partner_name}</p>
            {its.map((i) => <p key={i.offer.id} className="text-sm text-slate-400">{i.qty} × {i.offer.name} · {eur(i.offer[`price_${status}`])}</p>)}
            <div className="mt-3 flex gap-2">
              {["shipping", "pickup"].filter((m) => m === "shipping" || partners[pid]?.pickup).map((m) => (
                <button key={m} data-testid={`delivery-${m}-${pid}`} onClick={() => setDelivery({ ...delivery, [pid]: m })}
                  className={`rounded-full border px-4 py-1.5 text-sm ${(delivery[pid] || "shipping") === m ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-white/10 text-slate-400"}`}>
                  {m === "shipping" ? `Spedizione ${shipFor(pid) > 0 ? eur(partners[pid]?.shipping_cost) : "gratuita"}` : "Ritiro in sede"}
                </button>
              ))}
            </div>
            {(delivery[pid] || "shipping") === "pickup" && <p className="mt-2 text-xs text-slate-500">Ritiro presso: {partners[pid]?.address}</p>}
          </div>
        ))}
        <p className="text-sm text-slate-400">{needsAddress ? "Indirizzo di spedizione" : "I tuoi dati per il ritiro"}</p>
        <AddressFields value={address} onChange={setAddress} phone={phone} setPhone={setPhone} />
      </div>
      <div className="h-fit space-y-4 lg:sticky lg:top-24">
        <PointsSelector me={me} payable={total} enabled={enabled} setEnabled={setEnabled} points={points} setPoints={setPoints} />
        <PaymentMethods />
        <div className="space-y-1 rounded-2xl border border-white/10 bg-[#12141C] p-5">
          <SummaryRow label="Prodotti" value={eur(subtotal)} />
          <SummaryRow label="Spedizioni" value={eur(shipping)} />
          {discount > 0 && <SummaryRow label="Sconto punti" value={`−${eur(discount)}`} testid="summary-discount" />}
          <SummaryRow label="Da pagare con bonifico" value={eur(total - discount)} strong testid="summary-amount-due" />
        </div>
        <button data-testid="checkout-submit" disabled={busy} onClick={submit} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-amber-500 font-semibold text-black hover:bg-amber-400 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Conferma ordine
        </button>
      </div>
    </div>
  );
}
